import 'whatwg-fetch';
import auth from './AuthHelpers';
import { isPrimaryUrl, rewriteLegacyUrl } from 'ui/lib/servers';
import { notifyUnauthorized } from 'ui/lib/session';
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
function parseJSON(response) {
  return response.json ? response.json() : response;
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
function checkStatus(response, url) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  // Expired or revoked session: sign out once instead of failing silently.
  // Only the MTN (login) server's 401 means that; the Airtel servers get the
  // token copied a moment after sign-in, so their 401 is just an error.
  if (response.status === 401 && isPrimaryUrl(url)) {
    notifyUnauthorized();
  }

  return parseJSON(response).then(responseFormatted => {
    const error = new Error(response.statusText);
    error.response = response;
    error.response.payload = responseFormatted;
    throw error;
  });
}

/**
 * Format query params
 *
 * @param params
 * @returns {string}
 */
function formatQueryParams(params) {
  return Object.keys(params)
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export default function request(url, options = {}) {
  // Extract the body from options
  const { body, authType, responseType, ...restOptions } = options;

  // Set headers
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...restOptions.headers,
  };

  const userInfo = auth.getUserInfo();
  const accessToken = auth.getToken();

  const selectedBearer = authType === 'apiKey' ? userInfo?.api_key : accessToken;
  if (selectedBearer) {
    headers.Authorization = `Bearer ${selectedBearer}`;
  }

  let fetchOptions = { ...restOptions, headers };

  if (body instanceof FormData) {
    // If body is FormData, don't stringify it and remove Content-Type header
    fetchOptions = { ...fetchOptions, body };
    delete fetchOptions.headers['Content-Type'];
  } else if (body && typeof body === 'object') {
    // Stringify body object
    fetchOptions.body = JSON.stringify(body);
  }

  // Report reads follow the MTN / Airtel & 9mobile server switch.
  url = rewriteLegacyUrl(url);

  if (fetchOptions.params) {
    const params = formatQueryParams(fetchOptions.params);
    url = `${url}?${params}`;
  }

  if (responseType === 'blob') {
    return fetch(url, fetchOptions).then((r) => checkStatus(r, url)).then((response) => response.blob());
  }

  return fetch(url, fetchOptions).then((r) => checkStatus(r, url)).then(parseJSON);
}
